import { supabaseAdmin } from './supabase';

// Test connection to Supabase
export const testConnection = async () => {
  try {
    console.log('Testing Supabase connection...');
    const { data, error, count } = await supabaseAdmin
      .from('saas_students_marks')
      .select('*', { count: 'exact', head: true })
      .limit(1);
    
    if (error) {
      console.error('Connection test failed:', error);
      return { success: false, error: error.message };
    }
    
    console.log('Connection test successful, count:', count);
    return { success: true, data: { count } };
  } catch (err) {
    console.error('Connection test error:', err);
    return { success: false, error: err.message };
  }
};

// Grade boundaries for different grade levels
export const gradeBoundaries = {
  'A+': { min: 90, max: 100, gpa: 4.0, color: '#059669' },
  'A': { min: 85, max: 89, gpa: 4.0, color: '#059669' },
  'A-': { min: 80, max: 84, gpa: 3.7, color: '#16a34a' },
  'B+': { min: 75, max: 79, gpa: 3.3, color: '#65a30d' },
  'B': { min: 70, max: 74, gpa: 3.0, color: '#ca8a04' },
  'B-': { min: 65, max: 69, gpa: 2.7, color: '#d97706' },
  'C+': { min: 60, max: 64, gpa: 2.3, color: '#ea580c' },
  'C': { min: 55, max: 59, gpa: 2.0, color: '#dc2626' },
  'C-': { min: 50, max: 54, gpa: 1.7, color: '#dc2626' },
  'D+': { min: 45, max: 49, gpa: 1.3, color: '#991b1b' },
  'D': { min: 40, max: 44, gpa: 1.0, color: '#991b1b' },
  'F': { min: 0, max: 39, gpa: 0.0, color: '#7f1d1d' }
};

// Get grade from score
export const getGradeFromScore = (score) => {
  for (const [grade, boundary] of Object.entries(gradeBoundaries)) {
    if (score >= boundary.min && score <= boundary.max) {
      return grade;
    }
  }
  return 'F';
};

// Get GPA from score
export const getGPAFromScore = (score) => {
  const grade = getGradeFromScore(score);
  return gradeBoundaries[grade]?.gpa || 0.0;
};

// Fetch student performance summary data
export const fetchStudentPerformanceSummary = async (tenantId, academicYear = '2024-2025') => {
  try {
    const { data, error } = await supabaseAdmin
      .from('student_performance_summary')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('academic_year', academicYear)
      .order('class_rank', { ascending: true });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching student performance summary:', error);
    return { data: null, error };
  }
};

// Fetch subject configurations
export const fetchSubjectConfigurations = async (formLevel) => {
  try {
    console.log('fetchSubjectConfigurations called with:', { formLevel });
    
    const { data, error } = await supabaseAdmin
      .from('subject_configurations')
      .select('*')
      .eq('form_level', formLevel)
      .eq('is_active', true)
      .order('subject_weight', { ascending: false });

    console.log('fetchSubjectConfigurations result:', { data: data?.length, error });
    
    if (error) {
      console.error('Supabase error in fetchSubjectConfigurations:', error);
      throw error;
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching subject configurations:', error);
    return { data: null, error: error.message || error };
  }
};

// Fetch detailed student marks for a specific class
export const fetchClassStudentMarks = async (tenantId, classId, academicYear = '2024-2025') => {
  try {
    console.log('fetchClassStudentMarks called with:', { tenantId, classId, academicYear });
    
    // First, let's try without the join to see if that's the issue
    const { data: marksData, error: marksError } = await supabaseAdmin
      .from('saas_students_marks')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('class_id', classId)
      .eq('academic_year', academicYear)
      .order('student_id');

    console.log('Marks query result:', { data: marksData?.length, error: marksError });
    
    if (marksError) {
      console.error('Supabase error in fetchClassStudentMarks (marks):', marksError);
      return { data: null, error: marksError.message || marksError };
    }
    
    if (!marksData || marksData.length === 0) {
      console.log('No marks data found');
      return { data: [], error: null };
    }
    
    // Get unique student IDs from the marks data
    const studentIds = [...new Set(marksData.map(mark => mark.student_id))];
    console.log('Student IDs found:', studentIds.length);
    
    // Fetch student information separately
    const { data: studentsData, error: studentsError } = await supabaseAdmin
      .from('saas_students')
      .select('student_id, name, student_code')
      .in('student_id', studentIds);
    
    console.log('Students query result:', { data: studentsData?.length, error: studentsError });
    
    // Create a lookup map for student info
    const studentLookup = {};
    if (studentsData) {
      studentsData.forEach(student => {
        studentLookup[student.student_id] = student;
      });
    }
    
    // Combine the data
    const combinedData = marksData.map(mark => ({
      ...mark,
      saas_students: studentLookup[mark.student_id] || null
    }));
    
    console.log('Combined data result:', combinedData.length);
    
    return { data: combinedData, error: null };
  } catch (error) {
    console.error('Error fetching class student marks:', error);
    return { data: null, error: error.message || error };
  }
};

// Fetch available classes for a tenant
export const fetchTenantClasses = async (tenantId) => {
  try {
    console.log('fetchTenantClasses called with:', { tenantId });
    
    const { data, error } = await supabaseAdmin
      .from('saas_classes')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('name');

    console.log('fetchTenantClasses result:', { data: data?.length, error });
    
    if (error) {
      console.error('Supabase error in fetchTenantClasses:', error);
      throw error;
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching tenant classes:', error);
    return { data: null, error: error.message || error };
  }
};

// Process student marks data for display
export const processStudentMarksData = (marksData, subjects) => {
  console.log('Processing student marks data:', { marksData: marksData?.length, subjects: Object.keys(subjects || {}) });
  
  if (!marksData || !subjects) {
    console.log('Missing data:', { marksData: !!marksData, subjects: !!subjects });
    return [];
  }

  // Group marks by student
  const studentMarks = {};
  
  marksData.forEach(mark => {
    const studentId = mark.student_id;
    if (!studentMarks[studentId]) {
      studentMarks[studentId] = {
        id: studentId,
        studentNumber: mark.saas_students?.student_code || mark.student_id,
        name: mark.saas_students?.name || `Student ${mark.student_id}`,
        classId: mark.class_id,
        academicYear: mark.academic_year,
        subjects: {},
        totalScore: 0,
        subjectCount: 0
      };
    }

    // Add subject marks - check if subject exists in subjects config
    if (mark.subject_id) {
      const subject = subjects[mark.subject_id];
      if (subject) {
        const yearTotal = parseFloat(mark.year_total) || 0;
        
        studentMarks[studentId].subjects[mark.subject_id] = {
          name: subject.subject_name_zh,
          nameEn: subject.subject_name_en,
          color: subject.color_code,
          firstExam: mark.first_exam_score || 0,
          firstUT: mark.first_ut_score || 0,
          secondExam: mark.second_exam_score || 0,
          secondUT: mark.second_ut_score || 0,
          firstTermTotal: parseFloat(mark.first_term_total) || 0,
          secondTermTotal: parseFloat(mark.second_term_total) || 0,
          yearTotal: yearTotal,
          grade: getGradeFromScore(yearTotal),
          gpa: getGPAFromScore(yearTotal)
        };

        studentMarks[studentId].totalScore += yearTotal;
        studentMarks[studentId].subjectCount++;
      } else {
        console.log('Subject not found in config:', mark.subject_id);
      }
    }
  });

  console.log('Processed student marks:', Object.keys(studentMarks).length);

  // Calculate averages and rankings
  const processedStudents = Object.values(studentMarks).map(student => {
    const averageScore = student.subjectCount > 0 ? student.totalScore / student.subjectCount : 0;
    const overallGPA = student.subjectCount > 0 ? 
      Object.values(student.subjects).reduce((sum, subj) => sum + subj.gpa, 0) / student.subjectCount : 0;

    return {
      ...student,
      averageScore: Math.round(averageScore * 100) / 100,
      gpa: Math.round(overallGPA * 100) / 100
    };
  });

  console.log('Final processed students:', processedStudents.length);
  
  // Sort by average score for ranking
  return processedStudents.sort((a, b) => b.averageScore - a.averageScore);
};

// Calculate class statistics
export const calculateClassStatistics = (students, subjects) => {
  if (!students || students.length === 0) return {};

  const stats = {
    totalStudents: students.length,
    classAverage: 0,
    classGPA: 0,
    gradeDistribution: {},
    subjectStats: {}
  };

  // Calculate class averages
  const totalScore = students.reduce((sum, student) => sum + student.averageScore, 0);
  const totalGPA = students.reduce((sum, student) => sum + student.gpa, 0);
  
  stats.classAverage = Math.round(totalScore / students.length * 100) / 100;
  stats.classGPA = Math.round(totalGPA / students.length * 100) / 100;

  // Calculate grade distribution
  students.forEach(student => {
    const grade = getGradeFromScore(student.averageScore);
    stats.gradeDistribution[grade] = (stats.gradeDistribution[grade] || 0) + 1;
  });

  // Calculate subject statistics
  Object.keys(subjects).forEach(subjectId => {
    const subjectScores = students
      .map(student => student.subjects[subjectId]?.yearTotal)
      .filter(score => score !== undefined && score > 0);

    if (subjectScores.length > 0) {
      const avg = subjectScores.reduce((sum, score) => sum + score, 0) / subjectScores.length;
      const max = Math.max(...subjectScores);
      const min = Math.min(...subjectScores);
      const passCount = subjectScores.filter(score => score >= 50).length;
      const passRate = (passCount / subjectScores.length) * 100;

      stats.subjectStats[subjectId] = {
        average: Math.round(avg * 100) / 100,
        highest: max,
        lowest: min,
        passRate: Math.round(passRate * 100) / 100,
        totalStudents: subjectScores.length
      };
    }
  });

  return stats;
};

// Export grades to CSV
export const exportGradesToCSV = (students, subjects, className) => {
  const headers = ['學號', '姓名', '班級排名', '平均分', 'GPA'];
  Object.values(subjects).forEach(subject => {
    headers.push(`${subject.subject_name_zh}(總分)`, `${subject.subject_name_zh}(等級)`);
  });
  
  let content = headers.join(',') + '\n';
  
  students.forEach((student, index) => {
    const row = [
      student.studentNumber,
      `"${student.name}"`,
      index + 1,
      student.averageScore,
      student.gpa
    ];
    
    Object.keys(subjects).forEach(subjectId => {
      const subjectData = student.subjects[subjectId];
      if (subjectData) {
        row.push(subjectData.yearTotal, subjectData.grade);
      } else {
        row.push('', '');
      }
    });
    
    content += row.join(',') + '\n';
  });
  
  const filename = `${className}_成績表_${new Date().toISOString().split('T')[0]}.csv`;
  const mimeType = 'text/csv;charset=utf-8;';
  
  const element = document.createElement('a');
  const file = new Blob(['\uFEFF' + content], { type: mimeType });
  element.href = URL.createObjectURL(file);
  element.download = filename;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  
  return filename;
};
